import React, { useState, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, AlertCircle, X, RefreshCw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import adminService from "../services/adminService";
import { formatError } from "../utils/errorHandler";
import useCustomToast from "../hooks/useCustomToast";
import AuthContext from "../context/AuthContext";

// Predefined rejection reasons
const REJECTION_REASONS = [
  "Insufficient budget available",
  "Request does not meet policy guidelines",
  "Recipient is not eligible",
  "Duplicate request",
  "Incomplete information provided",
  "Other (please specify below)",
];

const ApprovalsPage = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const isSuperuserOrAdmin = user?.is_superuser || user?.role === "admin";
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectMemo, setRejectMemo] = useState("");

  // Fetch pending requests (backend returns enriched data with names)
  const { data: requests = [], isLoading: requestsLoading, error: requestsError, refetch } = useQuery({
    queryKey: ["pending-approvals"],
    queryFn: () => adminService.getPendingApprovals(),
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (requestId) => adminService.approveTransfer(requestId),
    onSuccess: () => {
      toast.success("Points transfer approved!");
      queryClient.invalidateQueries({ queryKey: ["pending-approvals"] });
    },
    onError: (error) => {
      toast.error(`Failed to approve: ${formatError(error)}`);
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ requestId, reason }) => adminService.rejectTransfer(requestId, reason),
    onSuccess: () => {
      toast.success("Points transfer rejected");
      closeRejectDialog();
      queryClient.invalidateQueries({ queryKey: ["pending-approvals"] });
    },
    onError: (error) => {
      toast.error(`Failed to reject: ${formatError(error)}`);
    },
  });

  const handleApprove = (request) => {
    if (!request.id) return;
    approveMutation.mutate(request.id);
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectReason("");
    setRejectMemo("");
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setSelectedRequest(null);
    setRejectDialogOpen(false);
    setRejectReason("");
    setRejectMemo("");
  };

  const handleReject = () => {
    if (!selectedRequest || !selectedRequest.id) return;

    // Combine reason and custom memo
    let fullReason = rejectReason;
    if (rejectMemo.trim()) {
      fullReason = rejectReason ? `${rejectReason}\n${rejectMemo}` : rejectMemo;
    }

    rejectMutation.mutate({
      requestId: selectedRequest.id,
      reason: fullReason || null,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8 pt-3 sm:pt-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Refresh button */}
        <div className="mb-2 sm:mb-6 flex items-center justify-end">
          <button
            onClick={() => refetch()}
            className="p-1 sm:p-2 hover:bg-gray-100 rounded-2xl transition-colors"
            title="Refresh approvals"
          >
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>

        {/* Error */}
        {requestsError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl">
            <p className="text-sm text-red-700">
              {formatError(requestsError)}
            </p>
          </div>
        )}

        {/* Content */}
        {requestsLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">
              No pending approvals at the moment. You're all caught up!
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        From → To
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Points
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Team
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[180px]">
                        Submitted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map((request) => {
                      const senderName = request.sender_name || request.sender_id;
                      const recipientName = request.recipient_name || request.recipient_id;
                      const teamName = request.team_name;
                      const createdAt = request.created_at
                        ? new Date(request.created_at).toLocaleString()
                        : "";
                      const isProcessing = approveMutation.isPending || rejectMutation.isPending;

                      return (
                        <tr
                          key={request.id ?? `${request.sender_id}-${request.recipient_id}`}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <p className="font-medium text-gray-900 truncate max-w-[220px]">
                                <Link to={`/profile/${request.sender_id}`} className="hover:text-purple-600 transition">
                                  {senderName}
                                </Link>
                              </p>
                              <p className="text-sm text-gray-500 max-w-[220px]">
                                →{" "}
                                <Link to={`/profile/${request.recipient_id}`} className="hover:text-purple-600 transition">
                                  {recipientName}
                                </Link>
                                {request.co_recipient_names && request.co_recipient_names.length > 0 && (
                                  <span className="text-gray-400">
                                    {", "}
                                    {request.co_recipient_names.slice(0, 2).join(", ")}
                                    {request.co_recipient_names.length > 2 && ` +${request.co_recipient_names.length - 2} more`}
                                  </span>
                                )}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {request.amount} pts{request.batch_size > 1 ? ` × ${request.batch_size}` : ""}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              request.source === 'recognition'
                                ? 'bg-purple-100 text-purple-800'
                                : request.source === 'nomination'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {request.source === 'recognition' ? 'Recognition' :
                               request.source === 'nomination' ? 'Nomination' :
                               request.source || 'Transfer'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {teamName ? teamName : request.team_id ? request.team_id : "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              <p className="text-sm text-gray-900 whitespace-nowrap">{createdAt}</p>
                              {request.memo && (
                                <p className="text-sm text-gray-500">{request.memo}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex gap-2 flex-wrap">
                              <button
                                onClick={() => handleApprove(request)}
                                disabled={isProcessing}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {isProcessing ? (
                                  <>
                                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                                    Approving...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4" />
                                    Approve
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => openRejectDialog(request)}
                                disabled={isProcessing}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-600 hover:bg-red-50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <XCircle className="w-4 h-4" />
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile stacked cards */}
            <div className="sm:hidden space-y-3">
              {requests.map((request) => {
                const senderName = request.sender_name || request.sender_id;
                const recipientName = request.recipient_name || request.recipient_id;
                const teamName = request.team_name;
                const createdAt = request.created_at
                  ? new Date(request.created_at).toLocaleString()
                  : "";
                const isProcessing = approveMutation.isPending || rejectMutation.isPending;
                const sourceLabel = request.source === 'recognition' ? 'Recognition' :
                                    request.source === 'nomination' ? 'Nomination' :
                                    request.source || 'Transfer';
                const sourceClass = request.source === 'recognition'
                  ? 'bg-purple-100 text-purple-800'
                  : request.source === 'nomination'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-gray-100 text-gray-800';

                return (
                  <div
                    key={request.id ?? `${request.sender_id}-${request.recipient_id}`}
                    className="bg-white rounded-2xl shadow-md p-3 space-y-2.5"
                  >
                    {/* Row 1: recipient bold + points pill */}
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/profile/${request.recipient_id}`} className="text-sm font-semibold text-gray-900 hover:text-purple-600 truncate min-w-0 flex-1">
                        {recipientName}
                        {request.co_recipient_names && request.co_recipient_names.length > 0 && (
                          <span className="text-gray-400 font-normal">
                            {", "}
                            {request.co_recipient_names.slice(0, 2).join(", ")}
                            {request.co_recipient_names.length > 2 && ` +${request.co_recipient_names.length - 2} more`}
                          </span>
                        )}
                      </Link>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 flex-shrink-0">
                        {request.amount} pts{request.batch_size > 1 ? ` × ${request.batch_size}` : ""}
                      </span>
                    </div>

                    {/* Row 2: → sender + date/time on the right */}
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <p className="text-gray-500 truncate min-w-0 flex-1">
                        →{" "}
                        <Link to={`/profile/${request.sender_id}`} className="hover:text-purple-600">
                          {senderName}
                        </Link>
                      </p>
                      <span className="text-gray-500 flex-shrink-0 whitespace-nowrap">{createdAt}</span>
                    </div>

                    {/* Row 3: badges */}
                    <div className="flex items-center flex-wrap gap-1.5 text-xs">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${sourceClass}`}>
                        {sourceLabel}
                      </span>
                      {teamName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          {teamName}
                        </span>
                      )}
                    </div>

                    {request.memo && (
                      <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1.5">{request.memo}</p>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(request)}
                        disabled={isProcessing}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                            Approving
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Approve
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => openRejectDialog(request)}
                        disabled={isProcessing}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-red-600 border border-red-600 hover:bg-red-50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Reject Dialog */}
        <AnimatePresence>
          {rejectDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={closeRejectDialog}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white rounded-2xl shadow-xl w-full max-w-md"
              >
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-full">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Reject Points Transfer
                    </h2>
                  </div>
                  <button
                    onClick={closeRejectDialog}
                    className="p-1 hover:bg-gray-100 rounded-2xl transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <p className="text-sm text-gray-600">
                    {selectedRequest ? (
                      <>
                        Rejecting request from{" "}
                        <Link to={`/profile/${selectedRequest.sender_id}`} className="font-medium text-purple-600 hover:text-purple-800">
                          {selectedRequest.sender_name || selectedRequest.sender_id}
                        </Link>{" "}
                        to{" "}
                        <Link to={`/profile/${selectedRequest.recipient_id}`} className="font-medium text-purple-600 hover:text-purple-800">
                          {selectedRequest.recipient_name || selectedRequest.recipient_id}
                        </Link>
                        {selectedRequest.co_recipient_names?.length > 0 && (
                          <> and {selectedRequest.co_recipient_names.length} other{selectedRequest.co_recipient_names.length !== 1 ? "s" : ""}</>
                        )}{" "}
                        for {selectedRequest.amount} pts{selectedRequest.batch_size > 1 ? ` × ${selectedRequest.batch_size} recipients` : ""}.
                      </>
                    ) : (
                      "Select a request to reject."
                    )}
                  </p>

                  {/* Predefined Reason Dropdown */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Reason for Rejection
                    </label>
                    <select
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Select a reason...</option>
                      {REJECTION_REASONS.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Custom Message */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Additional Notes {rejectReason === "Other (please specify below)" && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      value={rejectMemo}
                      onChange={(e) => setRejectMemo(e.target.value)}
                      placeholder="Add any additional context or explanation..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                <div className="px-6 pb-6 flex gap-3">
                  <button
                    onClick={closeRejectDialog}
                    disabled={rejectMutation.isPending}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={
                      rejectMutation.isPending ||
                      !rejectReason ||
                      (rejectReason === "Other (please specify below)" && !rejectMemo.trim())
                    }
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {rejectMutation.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Rejecting...
                      </span>
                    ) : (
                      "Reject Request"
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ApprovalsPage;
